import type { SceneCrossSectionElement } from '.'

const DEFAULT_CROSS_SECTION_SIZE = 0.15

const hasShape = (crossSection: CrossSectionTimberOrCLTOrSteel): crossSection is CrossSection =>
  'shape' in crossSection

const crossSectionWidth = (crossSection: CrossSectionTimberOrCLTOrSteel): number =>
  hasShape(crossSection) ? crossSection.shape.width : DEFAULT_CROSS_SECTION_SIZE

const crossSectionHeight = (crossSection: CrossSectionTimberOrCLTOrSteel): number =>
  hasShape(crossSection) ? crossSection.shape.height : DEFAULT_CROSS_SECTION_SIZE

const isPositionForVertical = (element: unknown): element is PositionForVertical => {
  return (
    typeof element === 'object' &&
    element !== null &&
    'position_guid' in element &&
    'start' in element &&
    'end' in element &&
    'coordinate_system' in element
  )
}

const isShapeObjectLine = (element: unknown): element is ShapeObjectLine => {
  return (
    typeof element === 'object' &&
    element !== null &&
    'guid' in element &&
    'shape' in element &&
    'coordinate_system' in element &&
    typeof element.shape === 'object' &&
    element.shape !== null &&
    'start' in element.shape &&
    'end' in element.shape
  )
}

/**
 * Builds a renderable scene cross section from a configured cross section and the
 * geometry of the model element it is assigned to. Returns null when either the
 * cross section or a supported model element geometry is missing.
 */
export const buildSceneCrossSectionElement = (
  formElement: {
    crossSection: CrossSectionTimberOrCLTOrSteel
    elementGuid: string
    color?: string
    outlinesColor?: string
  },
  modelElement: ShapeObject | ShapeObjectLine | PositionForVertical | undefined,
): SceneCrossSectionElement | null => {
  const { crossSection, elementGuid, color, outlinesColor } = formElement
  if (!crossSection || !modelElement) return null

  const common = {
    height: crossSectionHeight(crossSection),
    width: crossSectionWidth(crossSection),
    crossSection,
    elementType: modelElement.element_type,
    color,
    outlinesColor,
  }

  if (isPositionForVertical(modelElement)) {
    return {
      ...common,
      id: elementGuid,
      start: modelElement.start,
      end: modelElement.end,
      coordinateSystem: modelElement.coordinate_system,
    }
  }

  if (isShapeObjectLine(modelElement)) {
    return {
      ...common,
      id: elementGuid,
      start: modelElement.shape.start,
      end: modelElement.shape.end,
      coordinateSystem: modelElement.coordinate_system,
    }
  }

  return null
}
